

import { useState, useEffect, useMemo } from "react";
import { 
  Calendar,
  Download,
  Users,
  Package,
  ChevronUp,
  ChevronDown,
  GripVertical,
  Rows3,
  LayoutList,
  Settings2,
  Check,
  Eye,
  Search,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Activity,
  FileText,
} from "lucide-react";
import { Link } from 'react-router-dom';

import DashboardLayout from "../../components/DashboardLayout";
import DateRangePicker from "../../components/DateRangePicker";
import { eventsApi, mastersApi } from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/data";
import { 
  QuickStatsCards, 
  EventFilters, 
  EventCard,
  EventTypeBadge,
  StatusBadge,
  ProfitIndicator,
} from "./components";

// Types
interface Event {
  id: number;
  name: string;
  event_type?: string;
  client?: {
    id: number;
    name: string;
  };
  venue?: {
    id: number;
    name: string;
  };
  package?: {
    id: number;
    name: string;
  };
  start_date: string;
  end_date?: string;
  status: 'draft' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled';
  guest_count?: number;
  total_amount?: number;
  paid_amount?: number;
  balance?: number;
  total_cost?: number;
  event_vendors?: Array<{
    id: number;
    vendor_id: number;
    total_amount: number;
  }>;
  services?: Array<{
    id: number;
    description: string;
    unit_price: number;
    quantity: number;
  }>;
  created_at?: string;
  financials?: {
    profit: number;
    profit_percent: number;
    is_projected: boolean;
    customer?: {
      total: number;
      paid: number;
      balance: number;
    };
    vendor_totals?: {
      billed: number;
      paid: number;
      balance: number;
    };
    service_totals?: {
      revenue: number;
      cost: number;
    };
  };
}

export default function EventsReportPage() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({
    from: "",
    to: "",
  });
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [eventTypeFilter, setEventTypeFilter] = useState<string>("");
  const [venueFilter, setVenueFilter] = useState<string>("");
  const [eventTypes, setEventTypes] = useState<Array<{ id: number; name: string }>>([]);
  const [venues, setVenues] = useState<Array<{ id: number; name: string }>>([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 25,
    total: 0,
  });

  // Datatable state
  const [sortField, setSortField] = useState<string>('start_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [columnVisibility, setColumnVisibility] = useState({
    type: true,
    event: true,
    venue: true,
    date: true,
    financials: true,
    status: true,
    actions: true,
  });
  const [density, setDensity] = useState<'compact' | 'comfortable'>('comfortable');
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [showColumnMenu, setShowColumnMenu] = useState(false);

  // Mobile optimizations
  const [isMobile, setIsMobile] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
  const [showFilters, setShowFilters] = useState(true);

  // Fetch events and filter options
  useEffect(() => {
    fetchEvents();
  }, [dateRange, statusFilter, eventTypeFilter, venueFilter, pagination.current_page, sortField, sortDirection]);

  // Load filter options (event types and venues)
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const [typesRes, venuesRes] = await Promise.all([
          mastersApi.eventTypes.list({ status: 'active' }),
          mastersApi.venues.list(),
        ]);
        if (typesRes?.success) setEventTypes(typesRes.data || []);
        if (venuesRes?.success) setVenues(venuesRes.data || []);
      } catch (error) {
        console.error('Failed to load filter options:', error);
      }
    };
    loadFilterOptions();
  }, []);

  // Close column menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.column-menu-container')) {
        setShowColumnMenu(false);
      }
    };
    if (showColumnMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showColumnMenu]);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      // Auto-switch to card view on very small screens
      if (window.innerWidth < 640) {
        setViewMode('cards');
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        per_page: String(pagination.per_page),
        page: String(pagination.current_page),
        order_by: sortField,
        order_dir: sortDirection,
      });

      if (dateRange.from) params.append('from_date', dateRange.from);
      if (dateRange.to) params.append('to_date', dateRange.to);
      if (statusFilter) params.append('status', statusFilter);
      if (eventTypeFilter) params.append('event_type', eventTypeFilter);
      if (venueFilter) params.append('venue_id', venueFilter);
      if (search) params.append('search', search);

      const response = await eventsApi.list(params.toString());
      
      if (response?.success && response.data) {
        setEvents(response.data.data || []);
        setPagination({
          current_page: response.data.current_page,
          last_page: response.data.last_page,
          per_page: response.data.per_page,
          total: response.data.total,
        });
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, current_page: 1 }));
    fetchEvents();
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, current_page: page }));
  };

  // Handle sort
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sort icon
  const getSortIcon = (field: string) => {
    if (sortField !== field) return <GripVertical className="w-3 h-3 text-stone-300" />;
    return sortDirection === 'asc' ? 
      <ChevronUp className="w-4 h-4 text-blue-600" /> : 
      <ChevronDown className="w-4 h-4 text-blue-600" />;
  };

  // Handle row selection
  const toggleRowSelection = (id: number) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleAllRows = () => {
    if (selectedRows.size === events.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(events.map(e => e.id)));
    }
  };

  // Handle column visibility toggle
  const toggleColumn = (key: keyof typeof columnVisibility) => {
    setColumnVisibility(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Density classes
  const densityClasses = {
    compact: {
      row: 'py-2',
      cell: 'px-3',
      text: 'text-xs',
      header: 'px-3 py-2',
    },
    comfortable: {
      row: 'py-3',
      cell: 'px-4',
      text: 'text-sm',
      header: 'px-4 py-3',
    },
  };

  // Quick Stats Calculations - Use financials data when available
  const stats = useMemo(() => {
    const totalEvents = events.length;
    
    // Use financials.customer.total/paid if available, otherwise fallback to direct fields
    const totalRevenue = events.reduce((sum, e) => {
      const amount = e.financials?.customer?.total ?? e.total_amount ?? 0;
      return sum + amount;
    }, 0);
    
    const totalPaid = events.reduce((sum, e) => {
      const paid = e.financials?.customer?.paid ?? e.paid_amount ?? 0;
      return sum + paid;
    }, 0);
    
    const totalCost = events.reduce((sum, e) => {
      // Use financials profit calculation if available
      const cost = e.financials ? 
        ((e.financials.customer?.total ?? 0) - e.financials.profit) : 
        (e.total_cost ?? 0);
      return sum + cost;
    }, 0);
    
    const totalProfit = events.reduce((sum, e) => sum + (e.financials?.profit ?? 0), 0);
    const avgProfitPercent = totalRevenue > 0 ? ((totalProfit / totalRevenue) * 100) : 0;
    
    // Status counts
    const statusCounts = events.reduce((acc, e) => {
      acc[e.status] = (acc[e.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    // Payment collection rate
    const collectionRate = totalRevenue > 0 ? ((totalPaid / totalRevenue) * 100) : 0;

    return {
      totalEvents,
      totalRevenue,
      totalPaid,
      totalProfit,
      avgProfitPercent,
      statusCounts,
      collectionRate,
    };
  }, [events]);

  // Handle bulk CSV export - Use financials data when available
  const handleBulkExportCSV = () => {
    const selectedEvents = events.filter(e => selectedRows.has(e.id));
    const eventsToExport = selectedRows.size > 0 ? selectedEvents : events;
    
    const headers = ['Event Name', 'Event Type', 'Client', 'Venue', 'Start Date', 'End Date', 'Status', 'Guests', 'Vendors', 'Services', 'Total Amount', 'Paid Amount', 'Balance', 'Profit'];
    const rows = eventsToExport.map(event => {
      // Use financials data when available
      const totalAmount = event.financials?.customer?.total ?? event.total_amount ?? 0;
      const paidAmount = event.financials?.customer?.paid ?? event.paid_amount ?? 0;
      const balance = event.financials?.customer?.balance ?? (totalAmount - paidAmount);
      const profit = event.financials?.profit ?? ((event.total_amount ?? 0) - (event.total_cost ?? 0));
      
      return [
        event.name,
        event.event_type || '-',
        event.client?.name || '-',
        event.venue?.name || '-',
        formatDate(event.start_date),
        event.end_date ? formatDate(event.end_date) : '-',
        event.status,
        event.guest_count || '-',
        event.event_vendors?.length || 0,
        event.services?.length || 0,
        totalAmount,
        paidAmount,
        balance,
        profit,
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `events-report-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setSelectedRows(new Set()); // Clear selection after export
  };

  return (
    <DashboardLayout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            <h1 className="text-lg font-bold text-stone-800">Event Reports</h1>
            <span className="text-sm text-stone-500 hidden sm:inline">
              ({pagination.total} events)
            </span>
          </div>
          <button
            onClick={handleBulkExportCSV}
            className="flex items-center gap-2 text-sm font-semibold text-white px-4 py-2 rounded-xl gold-gradient hover:shadow-md transition-shadow"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>

        {/* Quick Stats Cards */}
        {!loading && events.length > 0 && (
          <QuickStatsCards stats={stats} />
        )}

        {/* Filters */}
        <EventFilters
          search={search}
          setSearch={setSearch}
          dateRange={dateRange}
          setDateRange={setDateRange}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          eventTypeFilter={eventTypeFilter}
          setEventTypeFilter={setEventTypeFilter}
          venueFilter={venueFilter}
          setVenueFilter={setVenueFilter}
          eventTypes={eventTypes}
          venues={venues}
          onApply={() => {
            setPagination(prev => ({ ...prev, current_page: 1 }));
            fetchEvents();
          }}
        />

        {/* Events Datatable */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          {loading ? (
            <div className="p-8 space-y-4">
              {/* Skeleton loading */}
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex gap-4 animate-pulse">
                  <div className="w-4 h-4 bg-stone-200 rounded"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-stone-200 rounded w-1/4"></div>
                    <div className="h-3 bg-stone-200 rounded w-1/3"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : events.length === 0 ? (
            <div className="p-8 text-center">
              <Calendar className="w-12 h-12 text-stone-300 mx-auto mb-2" />
              <p className="text-sm text-stone-500">No events found for the selected criteria.</p>
            </div>
          ) : (
            <>
              {/* Table Toolbar */}
              <div className="px-4 py-3 border-b border-stone-200 bg-stone-50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  {/* Row Selection Count */}
                  {selectedRows.size > 0 && (
                    <span className="text-sm text-stone-600">
                      <strong>{selectedRows.size}</strong> selected
                    </span>
                  )}
                  
                  {/* Per Page Selector */}
                  <select
                    value={pagination.per_page}
                    onChange={(e) => {
                      setPagination(prev => ({ ...prev, per_page: Number(e.target.value), current_page: 1 }));
                    }}
                    className="text-xs border border-stone-300 rounded px-2 py-1 bg-white"
                  >
                    <option value={10}>10 per page</option>
                    <option value={25}>25 per page</option>
                    <option value={50}>50 per page</option>
                    <option value={100}>100 per page</option>
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  {/* View Mode Toggle - Mobile only */}
                  <div className="sm:hidden flex items-center bg-white border border-stone-300 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setViewMode('cards')}
                      className={`p-2 ${viewMode === 'cards' ? 'bg-blue-100 text-blue-600' : 'text-stone-500'}`}
                      title="Card view"
                    >
                      <LayoutList className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('table')}
                      className={`p-2 ${viewMode === 'table' ? 'bg-blue-100 text-blue-600' : 'text-stone-500'}`}
                      title="Table view"
                    >
                      <Rows3 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Density Toggle - Desktop only */}
                  <div className="hidden sm:flex items-center bg-white border border-stone-300 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setDensity('compact')}
                      className={`p-1.5 ${density === 'compact' ? 'bg-blue-100 text-blue-600' : 'text-stone-500 hover:text-stone-700'}`}
                      title="Compact view"
                    >
                      <Rows3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDensity('comfortable')}
                      className={`p-1.5 ${density === 'comfortable' ? 'bg-blue-100 text-blue-600' : 'text-stone-500 hover:text-stone-700'}`}
                      title="Comfortable view"
                    >
                      <LayoutList className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Column Visibility Menu */}
                  <div className="relative column-menu-container">
                    <button
                      onClick={() => setShowColumnMenu(!showColumnMenu)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-600 bg-white border border-stone-300 rounded-lg hover:bg-stone-50"
                    >
                      <Settings2 className="w-3.5 h-3.5" />
                      Columns
                    </button>
                    
                    {showColumnMenu && (
                      <div className="absolute right-0 mt-1 w-40 bg-white border border-stone-200 rounded-lg shadow-lg z-10">
                        {Object.entries(columnVisibility).map(([key, visible]) => (
                          <button
                            key={key}
                            onClick={() => toggleColumn(key as keyof typeof columnVisibility)}
                            className="w-full flex items-center justify-between px-3 py-2 text-xs text-stone-700 hover:bg-stone-50 first:rounded-t-lg last:rounded-b-lg"
                          >
                            <span className="capitalize">{key}</span>
                            {visible && <Check className="w-3.5 h-3.5 text-blue-600" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Data Display - Table or Cards */}
              {viewMode === 'cards' ? (
                /* Card View - Mobile Optimized */
                <div className="divide-y divide-stone-200 max-h-[calc(100vh-400px)] overflow-y-auto">
                  {events.map((event) => {
                    // Use financials data when available, fallback to direct fields
                    const totalAmount = event.financials?.customer?.total ?? event.total_amount ?? 0;
                    const paidAmount = event.financials?.customer?.paid ?? event.paid_amount ?? 0;
                    const paymentPercent = totalAmount > 0 ? (paidAmount / totalAmount) * 100 : 0;
                    const profit = event.financials?.profit ?? ((event.total_amount ?? 0) - (event.total_cost ?? 0));
                    const profitPercent = event.financials?.profit_percent ?? (totalAmount > 0 ? ((profit / totalAmount) * 100) : 0);
                    const isSelected = selectedRows.has(event.id);
                    
                    return (
                      <div 
                        key={event.id} 
                        className={`p-4 hover:bg-blue-50/30 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}
                      >
                        <div className="flex items-start gap-3">
                          {/* Selection Checkbox */}
                          <div className="pt-0.5">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleRowSelection(event.id)}
                              className="w-5 h-5 rounded border-stone-300 text-blue-600 focus:ring-blue-500"
                            />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            {/* Header: Event Name + Status */}
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="min-w-0">
                                <h3 className="font-semibold text-stone-800 truncate">{event.name}</h3>
                                <p className="text-sm text-stone-500">{event.client?.name || 'No client'}</p>
                              </div>
                              <StatusBadge status={event.status} />
                            </div>
                            
                            {/* Event Type */}
                            {event.event_type && (
                              <div className="mb-2">
                                <EventTypeBadge type={event.event_type} />
                              </div>
                            )}
                            
                            {/* Details Grid */}
                            <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                              <div>
                                <p className="text-xs text-stone-400">Date</p>
                                <p className="font-medium text-stone-700">{formatDate(event.start_date)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-stone-400">Venue</p>
                                <p className="font-medium text-stone-700 truncate">{event.venue?.name || '-'}</p>
                              </div>
                              {event.guest_count && event.guest_count > 0 && (
                                <div>
                                  <p className="text-xs text-stone-400">Guests</p>
                                  <p className="font-medium text-stone-700">{event.guest_count}</p>
                                </div>
                              )}
                            </div>
                            
                            {/* Financial Summary */}
                            <div className="bg-stone-50 rounded-lg p-3 mb-3">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm text-stone-500">Total</span>
                                <span className="font-bold text-stone-800">{formatCurrency(totalAmount)}</span>
                              </div>
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm text-stone-500">Paid</span>
                                <span className={`font-medium ${paidAmount > 0 ? 'text-emerald-600' : 'text-stone-400'}`}>
                                  {formatCurrency(paidAmount)}
                                </span>
                              </div>
                              {/* Payment Progress */}
                              <div className="h-2 bg-stone-200 rounded-full overflow-hidden mb-1">
                                <div 
                                  className={`h-full rounded-full ${paymentPercent >= 100 ? 'bg-emerald-500' : paymentPercent >= 50 ? 'bg-blue-500' : 'bg-amber-500'}`}
                                  style={{ width: `${Math.min(paymentPercent, 100)}%` }}
                                />
                              </div>
                              <p className="text-xs text-stone-400 text-right">{paymentPercent.toFixed(0)}% paid</p>
                              
                              {/* Profit */}
                              {profit !== 0 && (
                                <div className="mt-2 pt-2 border-t border-stone-200">
                                  <ProfitIndicator profit={profit} percent={profitPercent} isProjected={event.financials?.is_projected} />
                                </div>
                              )}
                            </div>
                            
                            {/* Action Button */}
                            <Link
                              to={`/reports/events/detail?id=${event.id}`}
                              className="flex items-center justify-center gap-2 w-full py-2.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors active:scale-[0.98]"
                            >
                              <Eye className="w-4 h-4" />
                              View Details
                            </Link>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* Table View - Desktop Optimized */
                <div className="overflow-x-auto max-h-[calc(100vh-400px)] overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-stone-50 border-b border-stone-200 sticky top-0 z-10">
                      <tr>
                        {/* Select All */}
                        <th className={`${densityClasses[density].header} w-10 text-center`}>
                          <input
                            type="checkbox"
                            checked={selectedRows.size === events.length && events.length > 0}
                            onChange={toggleAllRows}
                            className="w-4 h-4 rounded border-stone-300 text-blue-600 focus:ring-blue-500"
                          />
                        </th>
                        
                        {columnVisibility.type && (
                          <th 
                            onClick={() => handleSort('event_type')}
                            className={`${densityClasses[density].header} text-left text-xs font-semibold text-stone-600 uppercase tracking-wider cursor-pointer hover:bg-stone-100 transition-colors hidden sm:table-cell`}
                          >
                            <div className="flex items-center gap-1">
                              Type
                              {getSortIcon('event_type')}
                            </div>
                          </th>
                        )}
                        
                        {columnVisibility.event && (
                          <th 
                            onClick={() => handleSort('name')}
                            className={`${densityClasses[density].header} text-left text-xs font-semibold text-stone-600 uppercase tracking-wider cursor-pointer hover:bg-stone-100 transition-colors`}
                          >
                            <div className="flex items-center gap-1">
                              Event & Client
                              {getSortIcon('name')}
                            </div>
                          </th>
                        )}
                        
                        {columnVisibility.venue && (
                          <th 
                            onClick={() => handleSort('venue')}
                            className={`${densityClasses[density].header} text-left text-xs font-semibold text-stone-600 uppercase tracking-wider cursor-pointer hover:bg-stone-100 transition-colors hidden lg:table-cell`}
                          >
                            <div className="flex items-center gap-1">
                              Venue & Guests
                              {getSortIcon('venue')}
                            </div>
                          </th>
                        )}
                        
                        {columnVisibility.date && (
                          <th 
                            onClick={() => handleSort('start_date')}
                            className={`${densityClasses[density].header} text-left text-xs font-semibold text-stone-600 uppercase tracking-wider cursor-pointer hover:bg-stone-100 transition-colors`}
                          >
                            <div className="flex items-center gap-1">
                              Date
                              {getSortIcon('start_date')}
                            </div>
                          </th>
                        )}
                        
                        {columnVisibility.financials && (
                          <th 
                            onClick={() => handleSort('total_amount')}
                            className={`${densityClasses[density].header} text-right text-xs font-semibold text-stone-600 uppercase tracking-wider cursor-pointer hover:bg-stone-100 transition-colors`}
                          >
                            <div className="flex items-center justify-end gap-1">
                              Financials
                              {getSortIcon('total_amount')}
                            </div>
                          </th>
                        )}
                        
                        {columnVisibility.status && (
                          <th 
                            onClick={() => handleSort('status')}
                            className={`${densityClasses[density].header} text-center text-xs font-semibold text-stone-600 uppercase tracking-wider cursor-pointer hover:bg-stone-100 transition-colors`}
                          >
                            <div className="flex items-center justify-center gap-1">
                              Status
                              {getSortIcon('status')}
                            </div>
                          </th>
                        )}
                        
                        {columnVisibility.actions && (
                          <th className={`${densityClasses[density].header} text-center text-xs font-semibold text-stone-600 uppercase tracking-wider`}>
                            Actions
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-200">
                      {events.map((event) => {
                        // Use financials data when available, fallback to direct fields
                        const totalAmount = event.financials?.customer?.total ?? event.total_amount ?? 0;
                        const paidAmount = event.financials?.customer?.paid ?? event.paid_amount ?? 0;
                        const balance = event.financials?.customer?.balance ?? (totalAmount - paidAmount);
                        const paymentPercent = totalAmount > 0 ? (paidAmount / totalAmount) * 100 : 0;
                        const vendorCount = event.event_vendors?.length || 0;
                        const serviceCount = event.services?.length || 0;
                        const profit = event.financials?.profit ?? ((event.total_amount ?? 0) - (event.total_cost ?? 0));
                        const profitPercent = event.financials?.profit_percent ?? (totalAmount > 0 ? ((profit / totalAmount) * 100) : 0);
                        const isSelected = selectedRows.has(event.id);
                        
                        return (
                          <tr 
                            key={event.id} 
                            className={`hover:bg-blue-50/50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}
                          >
                            {/* Row Selection */}
                            <td className={`${densityClasses[density].cell} ${densityClasses[density].row} w-10 text-center`}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleRowSelection(event.id)}
                                className="w-4 h-4 rounded border-stone-300 text-blue-600 focus:ring-blue-500"
                              />
                            </td>
                            
                            {/* Event Type */}
                            {columnVisibility.type && (
                              <td className={`${densityClasses[density].cell} ${densityClasses[density].row} hidden sm:table-cell`}>
                                <EventTypeBadge type={event.event_type} />
                              </td>
                            )}
                            
                            {/* Event & Client */}
                            {columnVisibility.event && (
                              <td className={`${densityClasses[density].cell} ${densityClasses[density].row}`}>
                                <div>
                                  <p className={`${densityClasses[density].text} font-semibold text-stone-800`}>{event.name}</p>
                                  <p className="text-xs text-stone-500 mt-0.5">{event.client?.name || 'No client'}</p>
                                  {!columnVisibility.type && (
                                    <div className="sm:hidden mt-1">
                                      <EventTypeBadge type={event.event_type} />
                                    </div>
                                  )}
                                </div>
                              </td>
                            )}
                            
                            {/* Venue & Guests */}
                            {columnVisibility.venue && (
                              <td className={`${densityClasses[density].cell} ${densityClasses[density].row} hidden lg:table-cell`}>
                                <div>
                                  <p className={densityClasses[density].text}>{event.venue?.name || '-'}</p>
                                  <div className="flex items-center gap-2 mt-1">
                                    {event.guest_count && event.guest_count > 0 && (
                                      <span className="inline-flex items-center text-xs text-stone-500">
                                        <Users className="w-3 h-3 mr-1" />
                                        {event.guest_count}
                                      </span>
                                    )}
                                    {(vendorCount > 0 || serviceCount > 0) && (
                                      <span className="inline-flex items-center text-xs text-stone-500">
                                        <Package className="w-3 h-3 mr-1" />
                                        {vendorCount + serviceCount} items
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </td>
                            )}
                            
                            {/* Date */}
                            {columnVisibility.date && (
                              <td className={`${densityClasses[density].cell} ${densityClasses[density].row}`}>
                                <div className={densityClasses[density].text}>
                                  <p className="font-medium">{formatDate(event.start_date)}</p>
                                  {event.end_date && event.end_date !== event.start_date && (
                                    <p className="text-xs text-stone-400">to {formatDate(event.end_date)}</p>
                                  )}
                                  {!columnVisibility.venue && (
                                    <div className="lg:hidden mt-1 text-xs text-stone-500">
                                      {event.venue?.name} {event.guest_count ? `• ${event.guest_count} guests` : ''}
                                    </div>
                                  )}
                                </div>
                              </td>
                            )}
                            
                            {/* Financial Summary */}
                            {columnVisibility.financials && (
                              <td className={`${densityClasses[density].cell} ${densityClasses[density].row}`}>
                                <div className="text-right space-y-1">
                                  <p className={`${densityClasses[density].text} font-semibold text-stone-800`}>{formatCurrency(totalAmount)}</p>
                                  <div className="flex items-center justify-end gap-2 text-xs">
                                    <span className="text-stone-500">Paid:</span>
                                    <span className={`font-medium ${paidAmount > 0 ? 'text-emerald-600' : 'text-stone-400'}`}>
                                      {formatCurrency(paidAmount)}
                                    </span>
                                  </div>
                                  {/* Payment Progress Bar */}
                                  <div className="w-full max-w-[120px] ml-auto">
                                    <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden">
                                      <div 
                                        className={`h-full rounded-full ${paymentPercent >= 100 ? 'bg-emerald-500' : paymentPercent >= 50 ? 'bg-blue-500' : 'bg-amber-500'}`}
                                        style={{ width: `${Math.min(paymentPercent, 100)}%` }}
                                      />
                                    </div>
                                    <p className="text-xs text-stone-400 mt-0.5">{paymentPercent.toFixed(0)}% paid</p>
                                  </div>
                                  {/* Profit indicator */}
                                  <div className="hidden sm:block mt-1">
                                    <ProfitIndicator 
                                      profit={profit} 
                                      percent={profitPercent} 
                                      isProjected={event.financials?.is_projected}
                                    />
                                  </div>
                                </div>
                              </td>
                            )}
                            
                            {/* Status */}
                            {columnVisibility.status && (
                              <td className={`${densityClasses[density].cell} ${densityClasses[density].row} text-center`}>
                                <StatusBadge status={event.status} />
                              </td>
                            )}
                            
                            {/* Actions */}
                            {columnVisibility.actions && (
                              <td className={`${densityClasses[density].cell} ${densityClasses[density].row} text-center`}>
                                <Link
                                  to={`/reports/events/detail?id=${event.id}`}
                                  className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                  <span className="hidden sm:inline">View</span>
                                </Link>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Enhanced Pagination - Mobile Optimized */}
              <div className="px-4 py-3 border-t border-stone-200 bg-stone-50 flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-xs text-stone-600 text-center sm:text-left">
                  <span className="sm:hidden">{pagination.current_page} / {pagination.last_page}</span>
                  <span className="hidden sm:inline">
                    Showing <strong>{(pagination.current_page - 1) * pagination.per_page + 1}</strong> to{' '}
                    <strong>{Math.min(pagination.current_page * pagination.per_page, pagination.total)}</strong> of{' '}
                    <strong>{pagination.total}</strong> events
                  </span>
                </p>
                
                <div className="flex items-center gap-1">
                  {/* Desktop: Full Pagination */}
                  <div className="hidden sm:flex items-center gap-1">
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={pagination.current_page === 1}
                      className="px-2 py-1 text-xs font-medium text-stone-600 bg-white border border-stone-300 rounded hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      First
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.current_page - 1)}
                      disabled={pagination.current_page === 1}
                      className="px-2 py-1 text-xs font-medium text-stone-600 bg-white border border-stone-300 rounded hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Prev
                    </button>
                    
                    {/* Page Number Buttons */}
                    {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                      let pageNum;
                      if (pagination.last_page <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.current_page <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.current_page >= pagination.last_page - 2) {
                        pageNum = pagination.last_page - 4 + i;
                      } else {
                        pageNum = pagination.current_page - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-8 h-7 text-xs font-medium rounded ${
                            pagination.current_page === pageNum
                              ? 'bg-blue-600 text-white'
                              : 'bg-white text-stone-600 border border-stone-300 hover:bg-stone-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => handlePageChange(pagination.current_page + 1)}
                      disabled={pagination.current_page === pagination.last_page}
                      className="px-2 py-1 text-xs font-medium text-stone-600 bg-white border border-stone-300 rounded hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.last_page)}
                      disabled={pagination.current_page === pagination.last_page}
                      className="px-2 py-1 text-xs font-medium text-stone-600 bg-white border border-stone-300 rounded hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Last
                    </button>
                  </div>

                  {/* Mobile: Simplified Pagination */}
                  <div className="flex sm:hidden items-center gap-2">
                    <button
                      onClick={() => handlePageChange(pagination.current_page - 1)}
                      disabled={pagination.current_page === 1}
                      className="px-3 py-2 text-sm font-medium text-stone-600 bg-white border border-stone-300 rounded-lg hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 transition-transform"
                    >
                      ← Prev
                    </button>
                    <span className="text-sm font-medium text-stone-700 px-2">
                      {pagination.current_page} / {pagination.last_page}
                    </span>
                    <button
                      onClick={() => handlePageChange(pagination.current_page + 1)}
                      disabled={pagination.current_page === pagination.last_page}
                      className="px-3 py-2 text-sm font-medium text-stone-600 bg-white border border-stone-300 rounded-lg hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 transition-transform"
                    >
                      Next →
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
